import { Paginator } from '@/components/Elements/Paginator/Paginator';
import { Card } from '@/components/ui/elements/card';
import { Heading2 } from '@/components/ui/typography/Typography';
import { ReactNode, useEffect } from 'react';

type OpportunityColumnProps = {
  title: string;
  subtitle: string;
  total: number;
  isLoading: boolean;
  page: number;
  lastPage: number;
  onPageChange: (page: number) => void;
  emptyLabel: string;
  children: ReactNode;
  testid?: string;
};

export const OpportunityColumn = ({
  title,
  subtitle,
  total,
  isLoading,
  page,
  lastPage,
  onPageChange,
  emptyLabel,
  children,
  testid,
}: OpportunityColumnProps) => {
  // A snooze/action can shrink the list out from under the current page
  // (e.g. snoozing the last item on page 2) — recover to the last valid
  // page instead of stranding the user on a blank one. Skipped while
  // loading: `lastPage` falls back to 1 before a not-yet-fetched page's
  // real data arrives, which would otherwise bounce every page > 1 back to
  // page 1 the instant it's requested.
  useEffect(() => {
    if (isLoading) return;
    if (lastPage >= 1 && page > lastPage) {
      onPageChange(lastPage);
    }
  }, [isLoading, page, lastPage, onPageChange]);

  return (
    <Card className="flex flex-col p-3" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <Heading2>{title}</Heading2>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <span className="rounded-full bg-muted px-2 py-0.5 text-sm font-semibold tabular-nums">
          {total}
        </span>
      </div>

      <div className="mt-3 flex-1 space-y-2">
        {isLoading ? (
          <>
            <div className="h-16 animate-pulse rounded bg-muted" />
            <div className="h-16 animate-pulse rounded bg-muted" />
          </>
        ) : total === 0 ? (
          <div className="rounded border border-dashed p-6 text-center text-sm text-muted-foreground">
            {emptyLabel}
          </div>
        ) : (
          children
        )}
      </div>

      {lastPage > 1 && (
        <Paginator page={page} lastPage={lastPage} onPageChange={onPageChange} className="mt-2" />
      )}
    </Card>
  );
};
